import type { SQLiteDatabase } from 'expo-sqlite';

import { newId, now } from '@/db/ids';
import type { NoteRow } from '@/db/types';
import { bumpDataRevision } from '@/lib/data-revision';

/**
 * Notes — free-form markdown jottings about a property. Lives next to
 * the Activity timeline on My Home and surfaces in /timeline as a
 * unified history (completed tasks + notes interleaved by date).
 *
 * The body is markdown (a small subset: headings, bold, italic, lists,
 * quotes, inline code). MarkdownView in src/components renders it.
 */

export interface NewNoteInput {
  propertyId: string;
  title?: string | null;
  body: string;
  pinned?: boolean;
}

export interface UpdateNoteInput {
  title?: string | null;
  body?: string;
  pinned?: boolean;
}

/**
 * Insert a new note. Empty bodies are rejected at the UI layer so this
 * just trusts the caller. Returns the inserted row's id.
 */
export async function addNote(
  db: SQLiteDatabase,
  input: NewNoteInput
): Promise<string> {
  const id = newId();
  const ts = now();
  await db.runAsync(
    `INSERT INTO note (id, property_id, title, body, pinned, created_at, updated_at)
     VALUES (?, ?, ?, ?, ?, ?, ?)`,
    id,
    input.propertyId,
    input.title?.trim() || null,
    input.body,
    input.pinned ? 1 : 0,
    ts,
    ts
  );
  bumpDataRevision();
  return id;
}

/**
 * List the active (non-deleted) notes for a property. Ordered with
 * pinned first, then most-recently-updated. Caller can pass a limit
 * to cap the result; defaults to 50 which covers the My Home preview
 * card and the /timeline page comfortably.
 */
export async function listNotes(
  db: SQLiteDatabase,
  propertyId: string,
  limit = 50
): Promise<NoteRow[]> {
  // Order: pinned first (still floats to top), then explicit sort_order
  // ASC (lower = earlier), then updated_at DESC as fallback for notes
  // that don't have an explicit order set yet. NULL sort_order sorts
  // AFTER any explicit values via the IS NULL trick — newly created
  // notes appear just below manually-ordered ones until the user
  // drag-drops them somewhere.
  return db.getAllAsync<NoteRow>(
    `SELECT * FROM note
       WHERE property_id = ? AND deleted_at IS NULL
       ORDER BY pinned DESC,
                CASE WHEN sort_order IS NULL THEN 1 ELSE 0 END,
                sort_order ASC,
                updated_at DESC
       LIMIT ?`,
    propertyId,
    limit
  );
}

/**
 * Persist a new manual order. Caller passes the full ordered ID list
 * (as it appears in the UI after a drag-drop). We rewrite sort_order
 * in 100-unit increments so a future insert-between can slot a new
 * value without renumbering everyone — same trick the design tools
 * use. updated_at is NOT bumped here because reordering shouldn't
 * resurface notes in the activity timeline.
 */
export async function reorderNotes(
  db: SQLiteDatabase,
  orderedIds: string[]
): Promise<void> {
  if (orderedIds.length === 0) return;
  // Single statement per id; SQLite is fast enough that batching via
  // a transaction wrapper is mostly cosmetic for sub-50-note lists.
  for (let i = 0; i < orderedIds.length; i += 1) {
    await db.runAsync(
      `UPDATE note SET sort_order = ? WHERE id = ?`,
      (i + 1) * 100,
      orderedIds[i]
    );
  }
}

export async function getNote(
  db: SQLiteDatabase,
  id: string
): Promise<NoteRow | null> {
  const row = await db.getFirstAsync<NoteRow>(
    `SELECT * FROM note WHERE id = ? AND deleted_at IS NULL`,
    id
  );
  return row ?? null;
}

export async function updateNote(
  db: SQLiteDatabase,
  id: string,
  patch: UpdateNoteInput
): Promise<void> {
  const sets: string[] = [];
  const args: (string | number | null)[] = [];
  if (patch.title !== undefined) {
    sets.push('title = ?');
    args.push(patch.title?.trim() || null);
  }
  if (patch.body !== undefined) {
    sets.push('body = ?');
    args.push(patch.body);
  }
  if (patch.pinned !== undefined) {
    sets.push('pinned = ?');
    args.push(patch.pinned ? 1 : 0);
  }
  if (sets.length === 0) return;
  sets.push('updated_at = ?');
  args.push(now());
  args.push(id);
  await db.runAsync(
    `UPDATE note SET ${sets.join(', ')} WHERE id = ?`,
    ...args
  );
  bumpDataRevision();
}

/** Soft delete — stamps deleted_at so cloud sync can tombstone cleanly. */
export async function deleteNote(
  db: SQLiteDatabase,
  id: string
): Promise<void> {
  const ts = now();
  await db.runAsync(
    `UPDATE note SET deleted_at = ?, updated_at = ? WHERE id = ?`,
    ts,
    ts,
    id
  );
  bumpDataRevision();
}

/**
 * Derive a display title from a note row. Prefers the explicit title,
 * falls back to the first heading or paragraph from the body. Bodies
 * are stored as HTML (Wave 35+) but legacy markdown is also handled
 * transparently.
 */
export function noteDisplayTitle(note: NoteRow): string {
  const explicit = note.title?.trim();
  if (explicit) return explicit;
  // Delegated to the renderer so HTML + markdown bodies both work.
  // Import locally to avoid pulling the renderer into modules that
  // only need the lib (e.g. notification helpers).
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { richTextTitle } = require('@/components/RichTextView');
  return richTextTitle(note.body);
}

/**
 * Short snippet for list previews. Strips HTML/markdown and clamps to
 * ~140 chars on a word boundary.
 */
export function noteSnippet(note: NoteRow, max = 140): string {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { richTextSnippet } = require('@/components/RichTextView');
  return richTextSnippet(note.body, max);
}
